import React, { useEffect, useMemo, useState } from "react";
import Header from "../components/layout/Header";
import { sectionsAPI } from "../services/sectionsAPI";
import "./styles/EmployerReferences.css";

const TAB_QUEST = "Questionnaires";
const TAB_REFS = "References";
const TABS = [TAB_QUEST, TAB_REFS];

const newQuestion = () => ({
  id: `q_${Date.now()}_${Math.random().toString(16).slice(2)}`,
  type: "short-answer",
  question: "",
  required: true,
  options: [],
});

const EmployerReferences = () => {
  const [activeTab, setActiveTab] = useState(TAB_QUEST);

  // templates (defaults)
  const [templates, setTemplates] = useState([]);
  const [tplLoading, setTplLoading] = useState(true);
  const [tplError, setTplError] = useState("");

  // responses
  const [employmentId, setEmploymentId] = useState(""); // employer must input/select
  const [responses, setResponses] = useState([]);
  const [respLoading, setRespLoading] = useState(false);
  const [respError, setRespError] = useState("");

  // create questionnaire modal
  const [createOpen, setCreateOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    description: "",
    questions: [newQuestion()],
  });
  const [qType, setQType] = useState("short-answer");
  const [qText, setQText] = useState("");

  const canSave = useMemo(() => {
    const hasName = form.name.trim().length >= 2;
    const validQuestions = form.questions.filter((q) => q.question.trim().length > 0);
    return hasName && validQuestions.length > 0;
  }, [form]);

  const loadDefaults = async () => {
    setTplLoading(true);
    setTplError("");
    try {
      const res = await sectionsAPI.questionnaire.getDefault();
      const data = res.data;
      setTemplates(Array.isArray(data) ? data : data ? [data] : []);
    } catch (e) {
      setTplError(e?.response?.data?.message || e.message || "Failed to load templates");
    } finally {
      setTplLoading(false);
    }
  };

  const loadResponses = async () => {
    if (!employmentId.trim()) {
      setRespError("Enter Employment ID to load submitted references.");
      return;
    }
    setRespLoading(true);
    setRespError("");
    try {
      const res = await sectionsAPI.reference.getResponses(employmentId.trim());
      setResponses(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setRespError(e?.response?.data?.message || e.message || "Failed to load responses");
    } finally {
      setRespLoading(false);
    }
  };

  useEffect(() => {
    loadDefaults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addQuestionToForm = () => {
    const question = qText.trim();
    if (!question) return;

    setForm((p) => ({
      ...p,
      questions: [
        ...p.questions,
        {
          ...newQuestion(),
          type: qType,
          question,
          required: true,
        },
      ],
    }));
    setQText("");
  };

  const saveQuestionnaire = async () => {
    if (!canSave) return;

    const questions = form.questions
      .filter((q) => q.question.trim().length > 0)
      .map((q) => ({
        id: q.id,
        question: q.question.trim(),
        type: q.type,
        required: q.required ?? true,
        options: q.options || [],
      }));

    setCreateLoading(true);
    try {
      await sectionsAPI.questionnaire.create({
        name: form.name.trim(),
        description: form.description?.trim() || undefined,
        type: "CUSTOM",
        questions,
      });

      setCreateOpen(false);
      setForm({ name: "", description: "", questions: [newQuestion()] });
      setQType("short-answer");
      setQText("");
      await loadDefaults();
    } catch (e) {
      // simple alert style to keep UI minimal
      alert(e?.response?.data?.message || e.message || "Failed to create questionnaire");
    } finally {
      setCreateLoading(false);
    }
  };

  return (
    <>
      <Header />
      <div className="er-page">
        <div className="er-container">
          <div className="er-head">
            <div className="er-brand">
              <div className="er-logo">▣</div>
              <div>
                <div className="er-title">Reference System</div>
                <div className="er-subtitle">Team & Transparent Reference Management</div>
              </div>
            </div>

            <div className="er-actions">
              {activeTab === TAB_QUEST && (
                <button className="er-btn er-btn-primary" onClick={() => setCreateOpen(true)}>
                  + Create Questionnaire
                </button>
              )}
            </div>
          </div>

          <div className="er-tabs">
            {TABS.map((t) => (
              <button
                key={t}
                className={`er-tab ${activeTab === t ? "active" : ""}`}
                onClick={() => setActiveTab(t)}
              >
                {t}
              </button>
            ))}
          </div>

          {activeTab === TAB_REFS ? (
            <div className="er-card">
              <div className="er-card-title">References</div>
              <div className="er-card-sub">View submitted references from candidates</div>

              <div className="er-row">
                <input
                  className="er-input"
                  value={employmentId}
                  onChange={(e) => setEmploymentId(e.target.value)}
                  placeholder="Enter Employment ID (employmentId)"
                />
                <button className="er-btn er-btn-secondary" onClick={loadResponses} disabled={respLoading}>
                  {respLoading ? "Loading..." : "Load"}
                </button>
              </div>

              {respError && <div className="er-error">{respError}</div>}

              <div className="er-emptywrap">
                {!responses.length ? (
                  <div className="er-empty">
                    <div className="er-empty-icon">📄</div>
                    <div>No reference requests found</div>
                  </div>
                ) : (
                  <div className="er-list">
                    {responses.map((r) => (
                      <div key={r.id} className="er-item">
                        <div className="er-item-title">{r.reference?.name || "Reference"}</div>
                        <div className="er-item-sub">{r.reference?.email}</div>
                        <div className="er-item-sub">Status: {r.reference?.status}</div>
                        <div className="er-item-sub">
                          Questionnaire: {r.questionnaireTemplate?.name || r.questionnaireTemplate?.title || "-"}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="er-card">
              <div className="er-card-title">Questionnaire Manager</div>
              <div className="er-card-sub">Create custom questionnaires or use templates for reference requests</div>

              <div className="er-section-title">Templates</div>
              <div className="er-section-sub">Pre-built questionnaires ready to use</div>

              {tplError && <div className="er-error">{tplError}</div>}
              {tplLoading ? (
                <div className="er-empty">Loading templates...</div>
              ) : (
                <div className="er-grid">
                  {templates.map((tpl) => (
                    <div key={tpl.id} className="er-template">
                      <div className="er-template-head">
                        <div>
                          <div className="er-template-title">{tpl.name}</div>
                          <div className="er-template-desc">{tpl.description}</div>
                        </div>
                        <div className="er-pill">Template</div>
                      </div>

                      <div className="er-template-count">
                        {(tpl.questions?.length || 0)} questions
                      </div>

                      <div className="er-template-qs">
                        {(tpl.questions || []).slice(0, 2).map((q) => (
                          <div key={q.id} className="er-template-q">
                            {q.question}
                          </div>
                        ))}
                        {(tpl.questions?.length || 0) > 2 && (
                          <div className="er-template-more">
                            +{(tpl.questions.length || 0) - 2} more questions
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* CREATE MODAL */}
        {createOpen && (
          <div className="er-modal-overlay">
            <div className="er-modal">
              <div className="er-modal-head">
                <div>
                  <div className="er-modal-title">Create Custom Questionnaire</div>
                  <div className="er-modal-sub">Build a custom questionnaire for reference requests</div>
                </div>
                <button className="er-modal-close" onClick={() => setCreateOpen(false)}>
                  ×
                </button>
              </div>

              <div className="er-modal-body">
                <div className="er-field">
                  <label className="er-label">Questionnaire Name</label>
                  <input
                    className="er-input"
                    value={form.name}
                    onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                    placeholder="e.g., Senior Developer Assessment"
                  />
                </div>

                <div className="er-field">
                  <label className="er-label">Description (Optional)</label>
                  <textarea
                    className="er-textarea"
                    value={form.description}
                    onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                    placeholder="Brief description of this questionnaire's purpose"
                  />
                </div>

                <div className="er-divider" />

                <div className="er-section-title">Add Question</div>

                <div className="er-field">
                  <label className="er-label">Question Type</label>
                  <select className="er-input" value={qType} onChange={(e) => setQType(e.target.value)}>
                    <option value="short-answer">Short Answer</option>
                    <option value="multiple-choice">Multiple Choice</option>
                  </select>
                </div>

                <div className="er-field">
                  <label className="er-label">Question</label>
                  <input
                    className="er-input"
                    value={qText}
                    onChange={(e) => setQText(e.target.value)}
                    placeholder="Enter your question here"
                  />
                </div>

                <button className="er-btn er-btn-secondary er-addq" onClick={addQuestionToForm}>
                  + Add Question
                </button>
              </div>

              <div className="er-modal-foot">
                <button className="er-btn er-btn-secondary" onClick={() => setCreateOpen(false)}>
                  Cancel
                </button>
                <button
                  className="er-btn er-btn-primary"
                  onClick={saveQuestionnaire}
                  disabled={!canSave || createLoading}
                >
                  {createLoading ? "Saving..." : "Save Questionnaire"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default EmployerReferences;
